import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DexScreenerModal from '../components/DexScreenerModal';
import TrackerDetailModal from '../components/TrackerDetailModal';
import TrackerGroupsPanel, { GroupsBadge } from '../components/TrackerGroupsPanel';
import TierBadge from '../components/TierBadge';
import { useAutoRefresh, AutoRefreshBadge } from '../hooks/useAutoRefresh';
import { formatScanSource } from '../utils/scanSource';
import { getTrackerRows } from '../utils/trackerUtils';

export default function Tokens() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('newly-added');
  const [timeRange, setTimeRange] = useState('30d');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [chartModal, setChartModal] = useState({ isOpen: false, chain: '', tokenAddress: '', tokenSymbol: '' });

  const [tierFilter, setTierFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [trackerModal, setTrackerModal] = useState({
    isOpen: false,
    tracker: null,
    tokenSymbol: '',
    currentMarketCap: null,
    recoverySnapshot: null,
  });

  const toggleExpanded = (rowKey) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const getTokenRowKey = (token) => `${token.chain}:${token.address}`;

  const openTrackerModal = ({ tracker, tokenSymbol, currentMarketCap, recoverySnapshot }) => {
    setTrackerModal({
      isOpen: true,
      tracker,
      tokenSymbol,
      currentMarketCap,
      recoverySnapshot,
    });
  };

  const tabs = [
    { id: 'newly-added', name: 'Newly Added', icon: '🆕' },
    { id: 'trending', name: 'Trending', icon: '🔥' },
    { id: 'performance', name: 'Best Performing', icon: '🚀' },
    { id: 'popular', name: 'Most Popular', icon: '⭐' },
    { id: 'from-ath', name: 'From ATH', icon: '📉' },
    { id: 'ath', name: 'ATH Leaders', icon: '💎' },
    { id: 'recent-ath', name: 'Recent ATH', icon: '📈' },
    { id: 'adoption', name: 'Fastest Growing', icon: '⚡' },
    { id: 'chains', name: 'By Chain', icon: '🔗' },
    { id: 'by-tier', name: 'By Tier', icon: '👑' },
  ];

  const fetchData = useCallback(async () => {
    setError('');
    try {
      let endpoint = `/analytics/tokens/${activeTab}`;
      const params = new URLSearchParams();

      // Add time range for applicable endpoints
      if (['performance', 'trending', 'adoption'].includes(activeTab)) {
        params.append('range', timeRange);
      }

      // Add tier filter for newly-added tab
      if (activeTab === 'newly-added' && tierFilter !== 'all') {
        params.append('tier', tierFilter);
      }

      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }

      const response = await api.get(endpoint);
      setData(response.data);
    } catch (err) {
      // Better error handling with specific messages
      if (err.response?.status === 401) {
        setError('Authentication required. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('Endpoint not found. Please refresh the page.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else if (err.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please check if the backend is running on port 5001.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to fetch token data');
      }
      console.error('Token analytics error:', err);
    }
  }, [activeTab, timeRange, tierFilter]);

  const { loading, refreshing, lastUpdated, refresh } = useAutoRefresh(fetchData, [fetchData]);

  const handleDelete = async (token) => {
    if (!window.confirm(`Remove "${token.symbol}" from token tracking? This will remove it from all chats and recovery.`)) return;
    const id = token._id || (token.id && token.id.toString && token.id.toString());
    setDeletingId(id || token.address);
    try {
      if (id && id.toString && id.toString().length === 24) {
        await api.delete(`/tokens/${id}`);
      } else if (token.address && token.chain) {
        await api.delete('/tokens/by-address', { params: { chain: token.chain, address: token.address } });
      } else {
        setError('Cannot delete: missing token id or address');
        return;
      }
      setError('');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete token');
    } finally {
      setDeletingId(null);
    }
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';

    // For very large percentages, show as multiplier (e.g., 1000% = 10x)
    if (value >= 10000) {
      const multiplier = (value / 100) + 1;
      if (multiplier >= 1000000000) {
        return `${(multiplier / 1000000000).toFixed(1)}Bx`;
      } else if (multiplier >= 1000000) {
        return `${(multiplier / 1000000).toFixed(1)}Mx`;
      } else if (multiplier >= 1000) {
        return `${(multiplier / 1000).toFixed(1)}Kx`;
      }
      return `${multiplier.toFixed(0)}x`;
    } else if (value >= 1000) {
      return `+${(value / 1000).toFixed(1)}K%`;
    }

    return `+${value.toFixed(2)}%`;
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    return value.toLocaleString();
  };

  const formatPrice = (value) => {
    if (!value && value !== 0) return 'N/A';

    // Format large numbers with K, M, B suffixes
    if (value >= 1_000_000_000) {
      return `$${(value / 1_000_000_000).toFixed(2)}B`;
    } else if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(2)}M`;
    } else if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(2)}K`;
    } else if (value >= 1) {
      return `$${value.toFixed(2)}`;
    } else if (value >= 0.01) {
      return `$${value.toFixed(4)}`;
    } else {
      return value.toExponential(4);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const getChainEmoji = (chain) => {
    const emojis = {
      solana: '◎',
      base: '🔵',
      ethereum: '⟠',
      bsc: '🟡',
    };
    return emojis[chain] || '🔗';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(text);
      setTimeout(() => setCopiedAddress(null), 2000);
    });
  };

  const generateDexScreenerUrl = (chain, address) => {
    const chainMap = {
      'solana': 'solana',
      'base': 'base',
      'ethereum': 'ethereum',
      'bsc': 'bsc'
    };
    const chainId = chainMap[chain] || chain;
    return `https://dexscreener.com/${chainId}/${address}`;
  };

  const generateTradeUrl = (chain, address) => {
    // Axiom supports Solana and BSC
    if (chain === 'solana' || chain === 'bsc') {
      return `https://axiom.trade/t/${address}`;
    }
    // GMGN.ai supports Base and Ethereum
    if (chain === 'base') {
      return `https://gmgn.ai/base/token/${address}`;
    }
    if (chain === 'ethereum') {
      return `https://gmgn.ai/eth/token/${address}`;
    }
    return null;
  };

  const getFomoUrl = (chain, address) => {
    const slug = { solana: 'solana', ethereum: 'ethereum', base: 'base', bsc: 'bnb' }[chain];
    return slug && address ? `https://fomo.family/tokens/${slug}/${address}` : null;
  };

  const getTradeButtonLabel = (chain) => {
    if (chain === 'solana' || chain === 'bsc') {
      return 'Axiom';
    }
    if (chain === 'base' || chain === 'ethereum') {
      return 'GMGN';
    }
    return 'Trade';
  };

  const renderTimeRangeSelector = () => {
    if (!['performance', 'trending', 'adoption'].includes(activeTab)) {
      return null;
    }

    return (
      <div className="flex space-x-2">
        {['7d', '30d', 'all'].map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`px-4 py-2 text-sm font-medium rounded ${
              timeRange === range
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
            }`}
          >
            {range === 'all' ? 'All Time' : range.toUpperCase()}
          </button>
        ))}
      </div>
    );
  };

  const renderTierFilterSelector = () => {
    if (activeTab !== 'newly-added') {
      return null;
    }

    return (
      <div className="flex space-x-2">
        {['all', 'starter', 'alpha', 'premium'].map((tier) => (
          <button
            key={tier}
            onClick={() => setTierFilter(tier)}
            className={`px-4 py-2 text-sm font-medium rounded ${
              tierFilter === tier
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
            }`}
          >
            {tier === 'all' ? 'All Tiers' : tier === 'starter' ? '🌱 Starter' : tier === 'alpha' ? '🅰️ Alpha' : tier === 'premium' ? '⭐ Premium' : tier}
          </button>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (loading && !data) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 rounded p-4">
          <p>{error}</p>
        </div>
      );
    }

    if (!data) return null;

    switch (activeTab) {
      case 'performance':
      case 'ath':
        return renderPerformanceTable();
      case 'trending':
      case 'adoption':
        return renderTrendingTable();
      case 'popular':
        return renderPopularTable();
      case 'newly-added':
        return renderNewlyAddedTable();
      case 'from-ath':
        return renderFromAthTable();
      case 'recent-ath':
        return renderRecentAthTable();
      case 'chains':
        return renderChainStats();
      case 'by-tier':
        return renderTierStats();
      default:
        return null;
    }
  };

  const renderPerformanceTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rank</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Chain</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">IMC</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Current MC</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ATH</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Gain %</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ATH Date</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {data.tokens?.map((token, index) => (
            <tr key={`${token.address}-${index}`} className="hover:bg-gray-50 dark:bg-gray-800/50">
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{index + 1}</td>
              <td className="px-4 py-4 whitespace-nowrap">
                <button
                  onClick={() => navigate(`/token/${token.chain}/${token.address}`)}
                  className="text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 -m-1 transition-colors"
                >
                  <div className="text-sm font-medium text-blue-600 hover:text-blue-800">{token.symbol}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{token.name}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{token.address?.slice(0, 8)}...{token.address?.slice(-6)}</div>
                </button>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                <span className="text-lg">{getChainEmoji(token.chain)}</span> {token.chain}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                <span title="Initial Market Cap">{formatPrice(token.initialMarketCap)}</span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                <span title="Current Market Cap">{formatPrice(token.currentMarketCap)}</span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatPrice(token.allTimeHigh)}</td>
              <td className="px-4 py-4 whitespace-nowrap">
                <span className={`text-sm font-bold ${token.athGainPercent > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                  {formatPercent(token.athGainPercent || token.priceChangePercent)}
                </span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{formatDate(token.allTimeHighDate)}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm">
                <div className="flex items-center gap-2">
                  {getFomoUrl(token.chain, token.address) && (
                    <a
                      href={getFomoUrl(token.chain, token.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                      title="Trade on Fomo"
                    >
                      Fomo
                    </a>
                  )}
                  {generateTradeUrl(token.chain, token.address) && (
                    <a
                      href={generateTradeUrl(token.chain, token.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                      title={`Trade on ${getTradeButtonLabel(token.chain)}`}
                    >
                      {getTradeButtonLabel(token.chain)}
                    </a>
                  )}
                  <button
                    onClick={() => setChartModal({ isOpen: true, chain: token.chain, tokenAddress: token.address, tokenSymbol: token.symbol })}
                    className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    title="View Chart"
                  >
                    📊 Chart
                  </button>
                  <button
                    onClick={() => copyToClipboard(token.address)}
                    className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                    title="Copy contract address"
                  >
                    {copiedAddress === token.address ? '✓ Copied' : '📋 Copy'}
                  </button>
                  <button
                    onClick={() => handleDelete(token)}
                    disabled={deletingId === (token._id?.toString?.() || token.address)}
                    className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                    title="Remove from tracking"
                  >
                    {deletingId === (token._id?.toString?.() || token.address) ? '…' : '🗑 Delete'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.tokens?.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No data available</div>
      )}
    </div>
  );

  const renderTrendingTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rank</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Chain</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tracked</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Unique Users</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Private/Group</th>
            {(activeTab === 'trending' || activeTab === 'adoption') && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Groups</th>
            )}
            {activeTab === 'trending' && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">MC Growth</th>
            )}
            {activeTab === 'adoption' && (
              <>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">MC Growth</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Multiplier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Adds/Day</th>
              </>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {data.tokens?.flatMap((token, index) => {
            const rowKey = getTokenRowKey(token);
            const isExpanded = expandedRows.has(rowKey);
            const colSpan = activeTab === 'adoption' ? 11 : 9;

            const mainRow = (
            <tr key={rowKey} className="hover:bg-gray-50 dark:bg-gray-800/50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{index + 1}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={() => navigate(`/token/${token.chain}/${token.address}`)}
                  className="text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 -m-1 transition-colors"
                >
                  <div className="text-sm font-medium text-blue-600 hover:text-blue-800">{token.symbol}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{token.name}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{token.address}</div>
                </button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                <span className="text-lg">{getChainEmoji(token.chain)}</span> {token.chain}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {formatNumber(token.trackedCount || token.totalAdoptions)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {formatNumber(token.uniqueUserCount)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                {token.privateChats}/{token.groupChats}
              </td>
              {(activeTab === 'trending' || activeTab === 'adoption') && (
                <td className="px-6 py-4 whitespace-nowrap">
                  <GroupsBadge
                    item={token}
                    expandedRows={expandedRows}
                    toggleExpanded={toggleExpanded}
                    rowKey={rowKey}
                  />
                </td>
              )}
              {activeTab === 'trending' && (
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                  {token.mcGrowthPercent != null ? formatPercent(token.mcGrowthPercent) : '—'}
                </td>
              )}
              {activeTab === 'adoption' && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                    {token.mcGrowthPercent != null ? formatPercent(token.mcGrowthPercent) : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {token.mcMultiplier != null ? `${token.mcMultiplier.toFixed(2)}x` : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                    {token.adoptionRate != null ? token.adoptionRate.toFixed(2) : '—'}
                  </td>
                </>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <div className="flex items-center gap-2">
                  {getFomoUrl(token.chain, token.address) && (
                    <a
                      href={getFomoUrl(token.chain, token.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                      title="Trade on Fomo"
                    >
                      Fomo
                    </a>
                  )}
                  {generateTradeUrl(token.chain, token.address) && (
                    <a
                      href={generateTradeUrl(token.chain, token.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                      title={`Trade on ${getTradeButtonLabel(token.chain)}`}
                    >
                      {getTradeButtonLabel(token.chain)}
                    </a>
                  )}
                  <button
                    onClick={() => setChartModal({ isOpen: true, chain: token.chain, tokenAddress: token.address, tokenSymbol: token.symbol })}
                    className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    title="View Chart"
                  >
                    📊 Chart
                  </button>
                  <button
                    onClick={() => copyToClipboard(token.address)}
                    className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                    title="Copy contract address"
                  >
                    {copiedAddress === token.address ? '✓ Copied' : '📋 Copy'}
                  </button>
                  <button
                    onClick={() => handleDelete(token)}
                    disabled={deletingId === (token._id?.toString?.() || token.address)}
                    className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                    title="Remove from tracking"
                  >
                    {deletingId === (token._id?.toString?.() || token.address) ? '…' : '🗑 Delete'}
                  </button>
                </div>
              </td>
            </tr>
            );

            const expandRow = isExpanded ? (
              <TrackerGroupsPanel
                key={`${rowKey}-groups`}
                item={token}
                tokenSymbol={token.symbol}
                currentMarketCap={token.currentMarketCap}
                onTrackerClick={openTrackerModal}
                colSpan={colSpan}
              />
            ) : null;

            return expandRow ? [mainRow, expandRow] : [mainRow];
          })}
        </tbody>
      </table>
      {data.tokens?.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No data available</div>
      )}
    </div>
  );

  const renderPopularTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rank</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Chain</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total Tracked</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Active</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {data.tokens?.map((token, index) => (
            <tr key={`${token.address}-${index}`} className="hover:bg-gray-50 dark:bg-gray-800/50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{index + 1}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={() => navigate(`/token/${token.chain}/${token.address}`)}
                  className="text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 -m-1 transition-colors"
                >
                  <div className="text-sm font-medium text-blue-600 hover:text-blue-800">{token.symbol}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{token.name}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{token.address}</div>
                </button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                <span className="text-lg">{getChainEmoji(token.chain)}</span> {token.chain}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {formatNumber(token.totalTracked)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                {formatNumber(token.activeTokens)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <div className="flex items-center gap-2">
                  {getFomoUrl(token.chain, token.address) && (
                    <a
                      href={getFomoUrl(token.chain, token.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                      title="Trade on Fomo"
                    >
                      Fomo
                    </a>
                  )}
                  {generateTradeUrl(token.chain, token.address) && (
                    <a
                      href={generateTradeUrl(token.chain, token.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                      title={`Trade on ${getTradeButtonLabel(token.chain)}`}
                    >
                      {getTradeButtonLabel(token.chain)}
                    </a>
                  )}
                  <button
                    onClick={() => setChartModal({ isOpen: true, chain: token.chain, tokenAddress: token.address, tokenSymbol: token.symbol })}
                    className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    title="View Chart"
                  >
                    📊 Chart
                  </button>
                  <button
                    onClick={() => copyToClipboard(token.address)}
                    className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                    title="Copy contract address"
                  >
                    {copiedAddress === token.address ? '✓ Copied' : '📋 Copy'}
                  </button>
                  <button
                    onClick={() => handleDelete(token)}
                    disabled={deletingId === (token._id?.toString?.() || token.address)}
                    className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                    title="Remove from tracking"
                  >
                    {deletingId === (token._id?.toString?.() || token.address) ? '…' : '🗑 Delete'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.tokens?.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No data available</div>
      )}
    </div>
  );

  const renderRecentAthTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Chain</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ATH</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Gain %</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ATH Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Scanned In</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {data.tokens?.map((token, index) => (
            <tr key={`${token.address}-${index}`} className="hover:bg-gray-50 dark:bg-gray-800/50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900 dark:text-white">{token.symbol}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{token.name}</div>
                <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{token.address}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                <span className="text-lg">{getChainEmoji(token.chain)}</span> {token.chain}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatPrice(token.allTimeHigh)}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm font-bold text-green-600">{formatPercent(token.athGainPercent)}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{formatDate(token.allTimeHighDate)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-xs">
                {(() => {
                  const source = formatScanSource(token);
                  return (
                    <span className={`px-2 py-1 rounded ${token.chatType === 'private' ? 'bg-blue-100 text-blue-800 dark:text-blue-300' : 'bg-purple-100 text-purple-800'}`}>
                      {source.label}
                    </span>
                  );
                })()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <div className="flex items-center gap-2">
                  {getFomoUrl(token.chain, token.address) && (
                    <a
                      href={getFomoUrl(token.chain, token.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                      title="Trade on Fomo"
                    >
                      Fomo
                    </a>
                  )}
                  {generateTradeUrl(token.chain, token.address) && (
                    <a
                      href={generateTradeUrl(token.chain, token.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                      title={`Trade on ${getTradeButtonLabel(token.chain)}`}
                    >
                      {getTradeButtonLabel(token.chain)}
                    </a>
                  )}
                  <button
                    onClick={() => setChartModal({ isOpen: true, chain: token.chain, tokenAddress: token.address, tokenSymbol: token.symbol })}
                    className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    title="View Chart"
                  >
                    📊 Chart
                  </button>
                  <button
                    onClick={() => copyToClipboard(token.address)}
                    className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                    title="Copy contract address"
                  >
                    {copiedAddress === token.address ? '✓ Copied' : '📋 Copy'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.tokens?.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No ATH hits in the last 7 days</div>
      )}
    </div>
  );

  const renderChainStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {data.chains?.map((chain) => (
        <div key={chain.chain} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white capitalize">{chain.chain}</h3>
            <span className="text-3xl">{getChainEmoji(chain.chain)}</span>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Tokens</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(chain.totalTokens)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Unique Tokens</p>
              <p className="text-xl font-semibold text-blue-600">{formatNumber(chain.uniqueTokenCount)}</p>
            </div>
            <div className="flex justify-between text-sm">
              <div>
                <p className="text-gray-500">Private</p>
                <p className="text-gray-900 dark:text-white font-medium">{formatNumber(chain.privateChats)}</p>
              </div>
              <div>
                <p className="text-gray-500">Group</p>
                <p className="text-gray-900 dark:text-white font-medium">{formatNumber(chain.groupChats)}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
      {data.chains?.length === 0 && (
        <div className="col-span-4 text-center py-8 text-gray-500 dark:text-gray-400">No chain data available</div>
      )}
    </div>
  );

  const renderNewlyAddedTable = () => (
    <div className="overflow-x-auto">
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        One row per token contract. Expand groups to see per-chat scan data — click a tracker for full details.
      </p>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Added</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Chain</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Groups</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tier</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">IMC</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Current MC</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Change</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">From ATH</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {data.tokens?.flatMap((token) => {
            const rowKey = getTokenRowKey(token);
            const trackers = getTrackerRows(token);
            const isExpanded = expandedRows.has(rowKey);
            const changePercent = token.initialMarketCap && token.currentMarketCap
              ? ((token.currentMarketCap - token.initialMarketCap) / token.initialMarketCap) * 100
              : 0;
            const primaryTracker = trackers[0];

            const mainRow = (
              <tr key={rowKey} className="hover:bg-gray-50 dark:bg-gray-800/50">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  <div>{formatDate(token.addedAt || token.createdAt)}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">{formatTimeAgo(token.addedAt || token.createdAt)}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <button
                    onClick={() => navigate(`/token/${token.chain}/${token.address}`)}
                    className="text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 -m-1 transition-colors"
                  >
                    <div className="text-sm font-medium text-blue-600 hover:text-blue-800">{token.symbol}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{token.name}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{token.address?.slice(0, 8)}...{token.address?.slice(-6)}</div>
                  </button>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <span className="text-lg">{getChainEmoji(token.chain)}</span> {token.chain}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <GroupsBadge
                    item={token}
                    expandedRows={expandedRows}
                    toggleExpanded={toggleExpanded}
                    rowKey={rowKey}
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <TierBadge tier={token.tier || primaryTracker?.tier} />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {formatPrice(token.initialMarketCap)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                  {formatPrice(token.currentMarketCap)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`text-sm font-bold ${changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {token.fromAthPercent !== null && token.fromAthPercent !== undefined ? (
                    <span className={`text-sm font-medium ${token.fromAthPercent <= -80 ? 'text-red-600' : token.fromAthPercent <= -50 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {token.fromAthPercent.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-2">
                    {getFomoUrl(token.chain, token.address) && (
                      <a
                        href={getFomoUrl(token.chain, token.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                        title="Trade on Fomo"
                      >
                        Fomo
                      </a>
                    )}
                    {generateTradeUrl(token.chain, token.address) && (
                      <a
                        href={generateTradeUrl(token.chain, token.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                        title={`Trade on ${getTradeButtonLabel(token.chain)}`}
                      >
                        {getTradeButtonLabel(token.chain)}
                      </a>
                    )}
                    <button
                      onClick={() => setChartModal({ isOpen: true, chain: token.chain, tokenAddress: token.address, tokenSymbol: token.symbol })}
                      className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                      title="View Chart"
                    >
                      Chart
                    </button>
                    <button
                      onClick={() => copyToClipboard(token.address)}
                      className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                      title="Copy contract address"
                    >
                      {copiedAddress === token.address ? '✓' : '📋'}
                    </button>
                    <button
                      onClick={() => handleDelete(token)}
                      disabled={deletingId === (token._id?.toString?.() || token.address)}
                      className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                      title="Remove from tracking"
                    >
                      {deletingId === (token._id?.toString?.() || token.address) ? '…' : '🗑 Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            );

            const expandRow = isExpanded ? (
              <TrackerGroupsPanel
                key={`${rowKey}-groups`}
                item={token}
                tokenSymbol={token.symbol}
                currentMarketCap={token.currentMarketCap}
                onTrackerClick={openTrackerModal}
                colSpan={10}
              />
            ) : null;

            return expandRow ? [mainRow, expandRow] : [mainRow];
          })}
        </tbody>
      </table>
      {data.tokens?.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No tokens found</div>
      )}
      {data.total != null && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 text-sm text-gray-600 dark:text-gray-400">
          Showing {data.tokens?.length} of {data.total} tokens
        </div>
      )}
    </div>
  );

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${Math.floor(diffDays / 7)}w ago`;
  };

  const formatAthPrice = (value) => {
    if (!value && value !== 0) return 'N/A';
    if (value >= 1) return `$${value.toFixed(4)}`;
    if (value >= 0.0001) return `$${value.toFixed(6)}`;
    if (value >= 0.00000001) return `$${value.toFixed(10)}`;
    return value.toExponential(4);
  };

  const renderFromAthTable = () => (
    <div>
      {/* Stats Summary */}
      {data.stats && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">Total with ATH</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{data.stats.total || 0}</div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4">
            <div className="text-sm text-red-600 dark:text-red-400">Deep Down (&gt;80%)</div>
            <div className="text-2xl font-bold text-red-600">{data.stats.deepDown || 0}</div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4">
            <div className="text-sm text-yellow-600 dark:text-yellow-400">Moderate (50-80%)</div>
            <div className="text-2xl font-bold text-yellow-600">{data.stats.moderateDown || 0}</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
            <div className="text-sm text-green-600 dark:text-green-400">Recovering (&lt;50%)</div>
            <div className="text-2xl font-bold text-green-600">{data.stats.recovering || 0}</div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
            <div className="text-sm text-blue-600 dark:text-blue-400">Avg From ATH</div>
            <div className="text-2xl font-bold text-blue-600">{data.stats.avgFromAth?.toFixed(1) || 0}%</div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Chain</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ATH Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">From ATH</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ATH Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Current MC</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {data.tokens?.map((token, index) => {
              const fromAth = token.fromAthPercent || 0;
              const fromAthColor = fromAth <= -80 ? 'text-red-600' : fromAth <= -50 ? 'text-yellow-600' : 'text-green-600';
              return (
                <tr key={`${token.address}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/token/${token.chain}/${token.address}`)}
                      className="text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 -m-1 transition-colors"
                    >
                      <div className="text-sm font-medium text-blue-600 hover:text-blue-800">{token.symbol}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{token.name}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{token.address?.slice(0, 8)}...{token.address?.slice(-6)}</div>
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <span className="text-lg">{getChainEmoji(token.chain)}</span> {token.chain}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 font-mono">
                    {formatAthPrice(token.athPrice)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`text-sm font-bold ${fromAthColor}`}>
                      {fromAth?.toFixed(1)}%
                    </span>
                    <div className="w-20 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 mt-1">
                      <div
                        className={`h-1.5 rounded-full ${fromAth <= -80 ? 'bg-red-500' : fromAth <= -50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(100, Math.abs(fromAth))}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(token.athDate)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                    {formatPrice(token.currentMarketCap)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`text-xs px-2 py-1 rounded ${token.athSource === 'geckoterminal' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {token.athSource || 'tracked'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      {getFomoUrl(token.chain, token.address) && (
                        <a
                          href={getFomoUrl(token.chain, token.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="Trade on Fomo"
                        >
                          Fomo
                        </a>
                      )}
                      {generateTradeUrl(token.chain, token.address) && (
                        <a
                          href={generateTradeUrl(token.chain, token.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                          title={`Trade on ${getTradeButtonLabel(token.chain)}`}
                        >
                          {getTradeButtonLabel(token.chain)}
                        </a>
                      )}
                      <button
                        onClick={() => setChartModal({ isOpen: true, chain: token.chain, tokenAddress: token.address, tokenSymbol: token.symbol })}
                        className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        title="View Chart"
                      >
                        Chart
                      </button>
                      <button
                        onClick={() => copyToClipboard(token.address)}
                        className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                        title="Copy contract address"
                      >
                        {copiedAddress === token.address ? '✓' : '📋'}
                      </button>
                      <button
                        onClick={() => handleDelete(token)}
                        disabled={deletingId === (token._id?.toString?.() || token.address)}
                        className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                        title="Remove from tracking"
                      >
                        {deletingId === (token._id?.toString?.() || token.address) ? '…' : '🗑 Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.tokens?.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No tokens with ATH data yet. Run the ATH update script to populate this data.
          </div>
        )}
        {data.total > 0 && (
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 text-sm text-gray-600 dark:text-gray-400">
            Showing {data.tokens?.length} of {data.total} tokens with ATH data
          </div>
        )}
      </div>
    </div>
  );

  const renderTierStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {data.tiers?.map((tier) => (
        <div key={tier.tier} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white capitalize mb-2">
              {tier.tier === 'starter' || tier.tier === 'free' ? '🌱' : tier.tier === 'alpha' ? '🅰️' : tier.tier === 'premium' || tier.tier === 'enterprise' ? '⭐' : '🌱'}{' '}
              {tier.tier === 'free' ? 'starter' : tier.tier === 'enterprise' ? 'premium' : tier.tier}
            </h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Tokens</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(tier.totalTokens)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Unique Tokens</p>
                <p className="text-xl font-semibold text-blue-600">{formatNumber(tier.totalUnique)}</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Chain Breakdown</p>
            <div className="space-y-1">
              {tier.chainBreakdown?.map((chain) => (
                <div key={chain.chain} className="flex justify-between text-sm">
                  <span className="text-gray-700">
                    {getChainEmoji(chain.chain)} {chain.chain}
                  </span>
                  <span className="text-gray-900 dark:text-white font-medium">{formatNumber(chain.count)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      {data.tiers?.length === 0 && (
        <div className="col-span-3 text-center py-8 text-gray-500 dark:text-gray-400">No tier data available</div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Token Analytics</h1>
          <AutoRefreshBadge refreshing={refreshing} lastUpdated={lastUpdated} className="mt-1" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {renderTimeRangeSelector()}
          {renderTierFilterSelector()}
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-4 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow ${refreshing ? 'ring-1 ring-blue-200 dark:ring-blue-800' : ''} transition-shadow`}>
        <div className="p-6">{renderContent()}</div>
      </div>

      {/* DexScreener Chart Modal */}
      <DexScreenerModal
        isOpen={chartModal.isOpen}
        onClose={() => setChartModal({ ...chartModal, isOpen: false })}
        chain={chartModal.chain}
        tokenAddress={chartModal.tokenAddress}
        tokenSymbol={chartModal.tokenSymbol}
      />

      <TrackerDetailModal
        isOpen={trackerModal.isOpen}
        onClose={() => setTrackerModal({ ...trackerModal, isOpen: false })}
        tracker={trackerModal.tracker}
        tokenSymbol={trackerModal.tokenSymbol}
        currentMarketCap={trackerModal.currentMarketCap}
        recoverySnapshot={trackerModal.recoverySnapshot}
      />
    </div>
  );
}
